import { Info, Minus, Plus } from "lucide-react";
import React, { useState } from "react";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgCoreTextColors,
  IgIconColors,
} from "@intelligems/ig-design-tokens/constants";
import { Box, ButtonBase, Stack, SxProps, Theme } from "@mui/material";

import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

export interface IgNumericalCounterProps {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: string;
  tooltipMessage?: string;
  onChange: (value: number | undefined) => void;
  sx?: SxProps<Theme>;
  /**
   * `"large"` (the default, and every caller's look before this prop
   * existed): 40px filled ± buttons around a 60px value cell.
   *
   * `"small"`: one 104×32 bordered control — transparent ± buttons with no
   * hover fill, a pressed plate on the pressed button only, a focus ring on
   * the whole control, and a reached min/max shown as 35% opacity on that
   * button rather than a disabled fill.
   */
  size?: "large" | "small";
}

const neutralBg = IgCoreBackgroundColors.interactive.neutral;

const getBorder = (disabled: boolean) =>
  `1px solid ${IgBorderColors.core[disabled ? "disabled" : "default"]}`;

const StepperButton = ({
  onClick,
  disabled,
  side,
}: {
  onClick: () => void;
  disabled: boolean;
  side: "left" | "right";
}) => (
  <ButtonBase
    disabled={disabled}
    onClick={onClick}
    aria-label={side === "left" ? "Decrease value" : "Increase value"}
    sx={{
      width: 40,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      border: getBorder(disabled),
      backgroundColor: disabled ? neutralBg.disabled : neutralBg.default,
      borderRadius: side === "left" ? "6px 0 0 6px" : "0 6px 6px 0",
      "&:hover": disabled ? {} : { backgroundColor: neutralBg.hover },
    }}
  >
    {side === "left" ? (
      <Minus
        size={16}
        color={disabled ? IgCoreTextColors.placeholder : IgIconColors.light}
      />
    ) : (
      <Plus
        size={16}
        color={disabled ? IgCoreTextColors.placeholder : IgIconColors.light}
      />
    )}
  </ButtonBase>
);

/** The small counter's ± button: transparent, with a pressed plate. */
const SmallStepperButton = ({
  onClick,
  disabled,
  atLimit,
  side,
}: {
  onClick: () => void;
  /** The whole control is disabled: icons stay at full colour. */
  disabled: boolean;
  /** This button's min/max is reached: the button fades instead. */
  atLimit: boolean;
  side: "left" | "right";
}) => (
  <ButtonBase
    disabled={disabled || atLimit}
    onClick={onClick}
    aria-label={side === "left" ? "Decrease value" : "Increase value"}
    sx={{
      position: "relative",
      // 31 + 40 + 31 fills the 102px inside the control's 1px border; the
      // design's 32px buttons overlap that border by a pixel.
      width: 31,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "transparent",
      opacity: atLimit && !disabled ? 0.35 : 1,
      // A 26px plate inset 3px from the 32px button, on the pressed one only.
      "&::before": {
        content: '""',
        position: "absolute",
        width: 26,
        height: 26,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: "4px",
      },
      "&:active::before": { backgroundColor: neutralBg.default },
      "& svg": { position: "relative" },
    }}
  >
    {side === "left" ? (
      <Minus size={16} color={IgIconColors.light} />
    ) : (
      <Plus size={16} color={IgIconColors.light} />
    )}
  </ButtonBase>
);

export const IgNumericalCounter = ({
  value,
  min,
  max,
  step = 1,
  disabled = false,
  label,
  tooltipMessage,
  onChange,
  sx,
  size = "large",
}: IgNumericalCounterProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const isAtMin = min !== undefined && value !== undefined && value <= min;
  const isAtMax = max !== undefined && value !== undefined && value >= max;
  const isSmall = size === "small";

  const valueInput = (
    <Box
      component="input"
      type="number"
      value={value ?? ""}
      disabled={disabled}
      step={step}
      min={min}
      max={max}
      onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
        onChange(ev.target.value === "" ? undefined : Number(ev.target.value))
      }
      onBlur={() => {
        setIsFocused(false);
        if (value === undefined) {
          return;
        }
        if (min !== undefined && value < min) {
          onChange(min);
        } else if (max !== undefined && value > max) {
          onChange(max);
        }
      }}
      onFocus={() => setIsFocused(true)}
      sx={{
        border: "none",
        ...(isSmall
          ? {
              width: 40,
              padding: 0,
              backgroundColor: "transparent",
            }
          : {
              width: 60,
              height: 40,
              borderTop: getBorder(disabled),
              borderBottom: getBorder(disabled),
              backgroundColor: disabled
                ? neutralBg.disabled
                : IgCoreBackgroundColors.baseLightHigh,
              padding: "0 12px",
            }),
        color: disabled
          ? IgCoreTextColors.placeholder
          : IgCoreTextColors.primary,
        textAlign: "center",
        fontSize: "14px",
        lineHeight: 1.5,
        outline: "none",
        fontFamily: "inherit",
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        MozAppearance: "textfield",
      }}
    />
  );

  const counter = isSmall ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        flexShrink: 0,
        boxSizing: "border-box",
        width: 104,
        height: 32,
        border: getBorder(disabled),
        borderRadius: "6px",
        overflow: "hidden",
        backgroundColor: IgCoreBackgroundColors.baseLightHigh,
        // The ring belongs to the whole control, drawn over its edge rather
        // than around the value cell alone.
        ...(isFocused &&
          !disabled && {
            boxShadow: `inset 0 0 0 2px ${IgBorderColors.accent.active}`,
          }),
        ...sx,
      }}
    >
      <SmallStepperButton
        onClick={() => onChange((value ?? min ?? 0) - step)}
        disabled={disabled}
        atLimit={isAtMin}
        side="left"
      />
      {valueInput}
      <SmallStepperButton
        onClick={() => onChange((value ?? min ?? 0) + step)}
        disabled={disabled}
        atLimit={isAtMax}
        side="right"
      />
    </Box>
  ) : (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "6px",
        overflow: "hidden",
        ...(isFocused &&
          !disabled && {
            outline: `2px solid ${IgBorderColors.accent.active}`,
            outlineOffset: -2,
          }),
        ...sx,
      }}
    >
      <StepperButton
        onClick={() => onChange((value ?? min ?? 0) - step)}
        disabled={disabled || isAtMin}
        side="left"
      />

      {valueInput}

      <StepperButton
        onClick={() => onChange((value ?? min ?? 0) + step)}
        disabled={disabled || isAtMax}
        side="right"
      />
    </Box>
  );

  if (label) {
    return (
      <Stack direction="row" spacing={5} alignItems="center">
        <Stack direction="row" spacing={isSmall ? 2 : 1} alignItems="center">
          <IgTypography>{label}</IgTypography>
          {tooltipMessage && (
            <IgTooltip title={tooltipMessage}>
              <Info size={16} />
            </IgTooltip>
          )}
        </Stack>
        {counter}
      </Stack>
    );
  }

  return counter;
};
